/**
 * Emerald Estate — Heritage
 *
 * Archetype: symmetrical and centred, the way a heritage estate agent has
 * printed particulars for a century. A double gold hairline frames the page,
 * the photograph is cut to an arch, and everything sits on the vertical centre
 * line. Symmetry only works if it is exact, so every x here is derived from the
 * canvas centre rather than typed.
 */

import { CANVAS, SPACE, RULE } from '../../system/tokens';
import { getPalette, scrim } from '../../system/palettes';
import { roles } from '../../system/typography';
import { text, shape, rule, image, page } from '../../system/elements';
import { statRow } from '../../system/blocks';
import { PHOTOS } from '../../system/photos';

const ID = 'tpl-flyer-real-estate-04';

export default {
  id: ID,
  meta: {
    name: 'Emerald Estate — Heritage',
    collection: 'signature',
    archetype: 'centred-symmetric',
    documentType: 'Flyer',
    industry: 'Real Estate',
    subCategory: 'Real Estate',
    canvas: CANVAS.usLetterPortrait,
    palette: 'emerald-gold',
    pairing: 'marcellus-lato',
    tags: ['real estate', 'flyer', 'estate', 'heritage', 'luxury', 'green', 'gold', 'classic', 'us letter'],
    description: 'Symmetrical heritage particulars: double gold hairline frame, arch-cut photograph, centred serif lockup on deep emerald.',
  },

  build() {
    const canvas = CANVAS.usLetterPortrait;
    const p = getPalette('emerald-gold');
    const r = roles('marcellus-lato');
    const els = [];

    const CX = canvas.width / 2;
    const OUTER = 110;                       // outer frame inset
    const INNER = OUTER + SPACE.xxs;         // inner frame inset
    const COL = 1900;                        // centred measure
    const colX = Math.round(CX - COL / 2);

    // ── Double hairline frame ───────────────────────────────────────────────
    const frameLine = (suffix, inset, weight) => {
      const w = canvas.width - inset * 2;
      const h = canvas.height - inset * 2;
      els.push(shape(`${ID}-frame-${suffix}-t`, { x: inset, y: inset, width: w, height: weight, fill: p.accent }));
      els.push(shape(`${ID}-frame-${suffix}-b`, { x: inset, y: inset + h - weight, width: w, height: weight, fill: p.accent }));
      els.push(shape(`${ID}-frame-${suffix}-l`, { x: inset, y: inset, width: weight, height: h, fill: p.accent }));
      els.push(shape(`${ID}-frame-${suffix}-r`, { x: inset + w - weight, y: inset, width: weight, height: h, fill: p.accent }));
    };
    frameLine('outer', OUTER, RULE.thin);
    frameLine('inner', INNER, RULE.hair);

    // ── Centred masthead ────────────────────────────────────────────────────
    let y = 260;

    const monogram = text(`${ID}-monogram`, r.h2, 'H & V', { x: colX, y, width: COL, align: 'center' },
      { color: p.accent, letterSpacing: 26 });
    els.push(monogram);
    y += monogram.height + SPACE.xxs;

    const house = text(`${ID}-house`, r.statLabel, 'Harbour & Vine · Est. 1962', { x: colX, y, width: COL, align: 'center' },
      { color: p.inkMuted });
    els.push(house);
    y += house.height + SPACE.lg;

    // Short centred rule.
    els.push(rule(`${ID}-rule-top`, { x: CX - 110, y, width: 220, weight: RULE.hair, color: scrim(p.ink, 0.45) }));
    y += SPACE.lg;

    // ── Arch-cut photograph ─────────────────────────────────────────────────
    const PW = 1560;
    const PH = 1080;
    els.push(image(`${ID}-hero`, {
      x: Math.round(CX - PW / 2), y, width: PW, height: PH, src: PHOTOS.timberDusk,
      cornerRadii: { tl: PW / 2, tr: PW / 2, br: 0, bl: 0 },
    }));
    y += PH + SPACE.xl;

    // ── Centred lockup ──────────────────────────────────────────────────────
    const eyebrow = text(`${ID}-eyebrow`, r.eyebrow, 'Presenting', { x: colX, y, width: COL, align: 'center' }, { color: p.accent });
    els.push(eyebrow);
    y += eyebrow.height + SPACE.sm;

    const headline = text(`${ID}-headline`, r.h1, 'Willow Bank Estate', { x: colX, y, width: COL, align: 'center' },
      { color: p.ink, fontSize: 170 });
    els.push(headline);
    y += headline.height + SPACE.xs;

    const place = text(`${ID}-place`, r.body, 'Litchfield County, Connecticut', { x: colX, y, width: COL, align: 'center' },
      { color: p.inkMuted });
    els.push(place);
    y += place.height + SPACE.lg;

    // ── Specification row ───────────────────────────────────────────────────
    const stats = statRow(ID, r, {
      x: colX, y, width: COL,
      stats: [
        { value: '6', label: 'Bedrooms' },
        { value: '5', label: 'Baths' },
        { value: '11', label: 'Acres' },
      ],
      valueColor: p.accent, labelColor: p.inkMuted, dividerColor: scrim(p.ink, 0.22),
    });
    els.push(...stats.elements);
    y = stats.y + SPACE.lg;

    els.push(rule(`${ID}-rule-bottom`, { x: CX - 110, y, width: 220, weight: RULE.hair, color: scrim(p.ink, 0.45) }));
    y += SPACE.md;

    const price = text(`${ID}-price`, r.h2, 'Guide price $4,600,000', { x: colX, y, width: COL, align: 'center' }, { color: p.ink });
    els.push(price);
    y += price.height + SPACE.sm;

    els.push(text(`${ID}-contact`, r.caption, 'Marielle Vance  ·  (860) 555-0119  ·  harbourvine.com', {
      x: colX, y, width: COL, align: 'center',
    }, { color: p.inkMuted }));

    return {
      canvas,
      pages: [page(`${ID}-page-1`, { name: 'Flyer', background: p.bg, elements: els })],
    };
  },
};
